import { createSelector } from 'reselect';
import { CloudActions } from '@portal/portal-core';

// utils is an untyped .js import; urlParameterValues's inferred return type is `{}`,
// so treat it as a plain map of url-parameter names to their string values.
import { urlParameterValues } from 'utils';

type UrlParameters = Record<string, string>;

/** The email verification slice of the store accessed by these selectors. */
interface EmailVerificationState {
  uiState?: string;
  userId?: string;
  emailAddress?: string;
}

/**
 * returns the UI state from url parameter if exists, else if exists in Redux state,
 * else returns empty string;
 * <p>
 * @param emailVerificationState - the email verification slice of the store
 */

function getEmailVerificationUi(emailVerificationState: EmailVerificationState): string {
  const parameters = urlParameterValues() as UrlParameters;
  const string = CloudActions.EMAIL_VERIFICATION_UNVERIFIED;
  const unverifiedFromUrl = parameters[string];
  const { uiState } = emailVerificationState;
  if (unverifiedFromUrl) {
    return string;
  }
  if (uiState) {
    return uiState;
  }
  return '';
}

/**
 * returns email verification state if exists, else returns null;
 * <p>
 * @param emailVerificationState - the email verification slice of the store
 */
export const getEmailVerificationUiState = getEmailVerificationUi;

function getEmailVerificationToken(memoized = true): unknown {
  const parameters = urlParameterValues() as UrlParameters;
  const tokenFromUrl = parameters.emailVerificationToken;
  if (memoized && parameters && tokenFromUrl) {
    // Legacy behavior preserved verbatim: a raw string is passed to createSelector.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: reselect expects input selectors, not a string.
    return createSelector(tokenFromUrl as any);
  }
  if (parameters && tokenFromUrl) {
    return tokenFromUrl;
  }
  return '';
}

/**
 * returns token from URL parameter else returns null;
 */

export const getEmailVerificationTokenState = getEmailVerificationToken;

function getUserId(emailVerificationState: EmailVerificationState): string {
  const parameters = urlParameterValues() as UrlParameters;
  const userIdFromUrl = parameters.userId;
  const userIdFromStore = emailVerificationState.userId;
  if (parameters && userIdFromUrl) {
    return userIdFromUrl;
  }
  if (userIdFromStore) {
    return userIdFromStore;
  }
  return '';
}

/**
 * returns userId from either URL parameter or email verification state if exists, else returns
 * null;
 * <p>
 * @param emailVerificationState - the email verification slice of the store
 */

export const getUserIdState = getUserId;

function getEmailAddress(emailVerificationState: EmailVerificationState): string {
  const parameters = urlParameterValues() as UrlParameters;
  const emailAddressFromUrl = parameters.emailAddress;
  const emailAddressFromStore = emailVerificationState.emailAddress;
  if (parameters && emailAddressFromUrl) {
    return emailAddressFromUrl;
  }
  if (emailAddressFromStore) {
    return emailAddressFromStore;
  }
  return '';
}

/**
 * returns emailAddress from either URL parameter or email verification state if exists, else
 * returns null;
 * <p>
 * @param emailVerificationState - the email verification slice of the store
 */

export const getEmailAddressState = getEmailAddress;
